import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { StaticValueRow, UpdateStaticValueRequest } from './static-values.model';

/**
 * Reads and updates Summary Report static values via the API.
 *
 * Response keys are lowercased so component bindings never depend on the API's
 * exact camelCase, mirroring {@link ReportDataService}.
 */
@Injectable({ providedIn: 'root' })
export class StaticValuesService {
  /** apiBaseUrl without any trailing slash. */
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');

  constructor(private readonly http: HttpClient) {}

  /** Loads all static-value records for the given reinsurance year. */
  getStaticValues(reinsuranceYear: number): Observable<StaticValueRow[]> {
    const url = `${this.base}/SummaryReportStaticValues/GetSummaryReportStaticValues?reinsuranceYear=${reinsuranceYear}`;
    return this.http
      .post<unknown[]>(url, null)
      .pipe(map((rows) => (rows ?? []).map((row) => normaliseRow(row))));
  }

  /**
   * Updates a record's editable fields (Value, Comment). The API also writes a
   * matching audit-log row in the same transaction. Returns the updated record.
   */
  updateStaticValue(request: UpdateStaticValueRequest): Observable<StaticValueRow> {
    const url = `${this.base}/SummaryReportStaticValues/UpdateStaticValue`;
    return this.http.put<unknown>(url, request).pipe(map((row) => normaliseRow(row)));
  }
}

/** Maps a raw API row to a {@link StaticValueRow}, lowercasing every top-level key. */
function normaliseRow(row: unknown): StaticValueRow {
  const out: Record<string, unknown> = {};
  if (row && typeof row === 'object') {
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
      out[key.toLowerCase()] = value;
    }
  }
  return {
    measure: (out['measure'] as string) ?? null,
    reinsuranceyear: (out['reinsuranceyear'] as number) ?? null,
    qtrstatus: (out['qtrstatus'] as string) ?? null,
    insuranceplanabbreviation: (out['insuranceplanabbreviation'] as string) ?? null,
    funddesignationflag: (out['funddesignationflag'] as string) ?? null,
    value: (out['value'] as number) ?? null,
    comment: (out['comment'] as string) ?? null,
    qtrrundate: (out['qtrrundate'] as string) ?? null
  };
}
